import { hseqRiskRating, hseqStageOf } from '@felix/domain';
import type { Content } from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import {
  pdfBlock,
  pdfFacts,
  pdfHeading,
  pdfLtUtc,
  pdfTable,
  pdfText,
  type RecordPdfApproval,
  type RecordPdfT,
  recordPdf,
} from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import { type FieldSpec, getPath } from './fields.js';
import {
  generalSpecs,
  initialSpecs,
  investigationSpecs,
  RISK_SPECS,
  sideSpecs,
  verificationSpecs,
} from './form-specs.js';
import type { HseDetail } from './types.js';

/** HSEQ formunun 10 basamağı (ADR 0097 §4): PDF bölümleri bu sırayla numaralanır. */
const HSEQ_SECTION_ORDER = [
  'details',
  'initial',
  'risk',
  'investigation',
  'plan',
  'linked',
  'implementation',
  'verification',
  'review',
  'closure',
];

/**
 * HSEQ raporunun PDF'i (ADR 0062 §3, ADR 0097 §4): ortak kabukta formun 10 basamağı sırasıyla
 * (Report Details, Initial Assessment, Risk Assessment, Investigation / Root Cause, Corrective
 * Action Plan, Linked Records, Implementation, Verification, Management / DPA Review, Closure) ve
 * onay izi (gönderen, kaptan incelemesi, kapanış gönderimi ve kararlar; ad, rol, zaman). Olay
 * zamanı LT + UTC. Sağlık alanı süzülmüşse "Restricted" basılır (ADR 0062 §1.3). Kaydedilmiş
 * içerikten üretilir; boş bölüm basılmaz.
 */
export interface HsePdfInput {
  detail: HseDetail;
  vessel: { name: string; imo?: string | null };
  brand: ReportBrand | null;
  printedAt: string;
}

/** Alanın basılan değeri; boşsa `null` (basılmaz). */
function shown(t: RecordPdfT, spec: FieldSpec, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  switch (spec.kind) {
    case 'select':
      return typeof value === 'string' || typeof value === 'number'
        ? t(`hse.${spec.optionsKey}.${value}`)
        : null;
    case 'bool':
      return t(value === true ? 'hse.yesNo.yes' : 'hse.yesNo.no');
    case 'date':
      return typeof value === 'string' ? formatDay(value) : null;
    default:
      return pdfText(value);
  }
}

/** Başlıklı alt grup; içerik yoksa hiç basılmaz. */
function withTitle(title: string, body: Content[]): Content[] {
  return body.length === 0 ? [] : [{ text: title, style: 'label', margin: [0, 6, 0, 2] }, ...body];
}

const labelOf = (t: RecordPdfT, spec: FieldSpec) =>
  t(`hse.fields.${spec.label ?? spec.path.split('.').pop()}`);

export function hsePdf(t: RecordPdfT, input: HsePdfInput) {
  const { detail, vessel } = input;
  const report = detail.report;
  const type = detail.reportType;
  const offset = report.tzOffsetMin;
  const hidden = new Set((detail.medical?.hidden ?? []).map((key) => `details.${key}`));
  const restricted = t('recordPdf.restricted');
  const k = (key: string, options?: Record<string, unknown>) => t(`hse.pdf.${key}`, options);
  const roleNames = (ids: readonly string[] | undefined) => {
    const names = (ids ?? []).map((id) => detail.roles[id]).filter((n): n is string => !!n);
    return names.length > 0 ? names.join(', ') : null;
  };
  const nameOf = (id: string | null | undefined) => (id ? (detail.names[id] ?? id) : null);

  /** Alan listesinden bilgi çiftleri ve uzun metinler; seçenek listesi (choices) olanlar atlanır. */
  const fieldContent = (specs: readonly FieldSpec[], skip: readonly string[] = []): Content[] => {
    const pairs: [string, string][] = [];
    const blocks: Content[] = [];
    for (const spec of specs) {
      if (spec.choices || skip.includes(spec.path)) continue;
      if (hidden.has(spec.path)) {
        pairs.push([labelOf(t, spec), restricted]);
        continue;
      }
      const value = shown(t, spec, getPath(report, spec.path));
      if (value === null) continue;
      if (spec.kind === 'textarea') blocks.push(...pdfBlock(labelOf(t, spec), value));
      else pairs.push([labelOf(t, spec), value]);
    }
    return [...(pairs.length > 0 ? [pdfFacts(pairs)] : []), ...blocks];
  };

  const content: Content[] = [];
  const section = (key: string, body: Content[]) => {
    if (body.length === 0) return;
    const no = HSEQ_SECTION_ORDER.indexOf(key) + 1;
    content.push(pdfHeading(`${no}. ${t(`hse.hseq.sections.${key}`)}`), ...body);
  };
  const details = (report.details ?? {}) as Record<string, unknown>;

  // 1. Report Details: genel bilgiler (olay zamanı LT + UTC) ve türe özgü alanlar
  const side = sideSpecs(report, null);
  const recurrence = detail.recurrence ?? [];
  section('details', [
    pdfFacts([
      [k('vessel'), vessel.imo ? `${vessel.name} (IMO ${vessel.imo})` : vessel.name],
      [k('type'), t(`hse.types.${type}`)],
      [k('occurred'), pdfLtUtc(report.occurredAt, offset)],
      [
        k('status'),
        `${t(`hse.hseq.stages.${hseqStageOf(detail.status)}`)} (${t(`hse.statuses.${detail.status}`)})`,
      ],
      [k('responsible'), pdfText(report.responsible)],
      [
        type === 'deficiency' ? t('hse.fields.closeBy') : k('targetDate'),
        report.targetDate ? formatDay(report.targetDate) : '—',
      ],
      ...(detail.links.equipment
        ? ([
            [k('equipment'), `${detail.links.equipment.code} · ${detail.links.equipment.name}`],
          ] as [string, string][])
        : []),
    ]),
    ...fieldContent(generalSpecs(type), ['title']),
    ...withTitle(
      side.key === 'psc' ? t('hse.record.side.psc') : t(`hse.hseq.typeDetails.${type}`),
      fieldContent(side.specs, ['targetDate']),
    ),
    ...(type === 'non_conformity'
      ? pdfBlock(
          t('hse.hseq.recurrence.title'),
          recurrence.length === 0
            ? t('hse.hseq.recurrence.none')
            : `${t('hse.hseq.recurrence.found', { count: recurrence.length })} ${recurrence
                .map((r) => r.reportNo)
                .join(', ')}`,
        )
      : []),
  ]);

  // 2. Initial Assessment: ilk müdahale, kaptanın değerlendirmesi ve incelemesi, kaza bildirimi
  const notification = (details.notification ?? null) as {
    decision?: string | null;
    reason?: string | null;
    items?: {
      recipient: string;
      name?: string | null;
      dueDate?: string | null;
      notifiedAt?: string | null;
      reference?: string | null;
    }[];
  } | null;
  const notificationContent: Content[] = [];
  if (type === 'accident' && notification?.decision) {
    notificationContent.push(
      { text: k('notification'), style: 'label', margin: [0, 6, 0, 2] },
      pdfFacts([
        [k('decision'), t(`hse.notificationDecisions.${notification.decision}`)],
        [k('reason'), pdfText(notification.reason)],
      ]),
    );
    const items = notification.items ?? [];
    if (items.length > 0) {
      notificationContent.push(
        pdfTable(
          [
            k('recipient'),
            k('name'),
            t('hse.notifications.dueDate'),
            k('notifiedAt'),
            k('reference'),
          ],
          items.map((item) => [
            t(`hse.recipients.${item.recipient}`),
            pdfText(item.name),
            item.dueDate ? formatDay(item.dueDate) : '—',
            pdfLtUtc(item.notifiedAt, offset),
            pdfText(item.reference),
          ]),
          [70, '*', 56, 100, 70],
        ),
      );
    }
  }
  // Kaptanın ilk değerlendirmesini yazan ve rütbesi; kaptan değilse "On behalf of Master"
  // (ADR 0127 §4)
  const assessor = detail.initialAssessmentAuthor;
  section('initial', [
    ...fieldContent(initialSpecs(type)),
    ...(assessor
      ? pdfBlock(
          k('assessedBy'),
          `${assessor.name ?? '—'} (${assessor.roleName ?? t('masterSection.noRank')}) · ${pdfLtUtc(assessor.at, assessor.tzOffsetMin)}${assessor.onBehalf ? ` · ${t('masterSection.onBehalf')}` : ''}`,
        )
      : []),
    ...(report.reviewedAt
      ? pdfBlock(
          k('reviewed'),
          `${nameOf(report.reviewedBy) ?? '—'} · ${pdfLtUtc(report.reviewedAt, offset)}${report.reviewNote ? `\n${report.reviewNote}` : ''}`,
        )
      : []),
    ...notificationContent,
  ]);

  // 3. Risk Assessment: seviye şirket matrisinden (ADR 0097 §4)
  const rating = hseqRiskRating(
    (details.risk ?? null) as { likelihood?: unknown; severity?: unknown } | null,
    detail.riskMatrix,
  );
  section('risk', [
    ...fieldContent(RISK_SPECS),
    ...(rating
      ? [
          pdfFacts([
            [
              t('hse.hseq.risk.level'),
              `${t(`hse.hseq.risk.levels.${rating.level}`)} (${t('hse.hseq.risk.score', { score: rating.score })})`,
            ],
          ]),
        ]
      : []),
  ]);

  // 4. Investigation / Root Cause
  section(
    'investigation',
    fieldContent(investigationSpecs(type, null), ['responsible', 'targetDate']),
  );
  if (hidden.size > 0) {
    content.push({ text: k('medicalRestricted'), style: 'restricted', margin: [0, 4, 0, 0] });
  }

  // 5. Corrective Action Plan: PSC bulguları ve faaliyetler
  const plan: Content[] = [];
  if (detail.findings.length > 0) {
    plan.push(
      { text: k('findings'), style: 'label', margin: [0, 6, 0, 2] },
      pdfTable(
        [k('no'), k('finding'), k('regulation'), k('code'), k('due'), k('state')],
        detail.findings.map((f) => [
          String(f.itemNo),
          f.description,
          pdfText(f.regulationRef),
          [f.code, f.actionTakenCode].filter(Boolean).join(' / ') || '—',
          f.dueDate ? formatDay(f.dueDate) : '—',
          t(`hse.findingStatuses.${f.status}`),
        ]),
        [16, '*', 70, 50, 52, 52],
      ),
    );
  }
  if (detail.actionList.length > 0) {
    plan.push(
      pdfTable(
        [k('no'), k('kind'), k('action'), k('responsible'), k('due'), k('state')],
        detail.actionList.map((a) => [
          String(a.actionNo),
          t(`hse.actionKinds.${a.kind}`),
          [
            a.description,
            a.completionNote ? `${k('completion')}: ${a.completionNote}` : null,
            a.cancelReason ? `${k('cancelled')}: ${a.cancelReason}` : null,
          ]
            .filter(Boolean)
            .join('\n'),
          a.responsible,
          formatDay(a.dueDate),
          t(`hse.actionStatuses.${a.status}`),
        ]),
        [16, 52, '*', 70, 52, 52],
      ),
    );
  }
  section('plan', plan);

  // 6. Linked Records
  const links: [string, string][] = [];
  if (detail.links.defect) links.push([k('defect'), detail.links.defect.no]);
  if (detail.links.workOrder) links.push([k('workOrder'), detail.links.workOrder.no]);
  const requisitions = detail.links.requisitions ?? [];
  if (requisitions.length > 0) {
    links.push([t('hse.hseq.linked.requisition'), requisitions.map((r) => r.no).join(', ')]);
  }
  if (detail.links.certificate)
    links.push([
      k('certificate'),
      `${detail.links.certificate.name} ${detail.links.certificate.number}`,
    ]);
  if (detail.links.formRecord) links.push([k('formRecord'), detail.links.formRecord.no]);
  if (detail.related.parent)
    links.push([k('parent'), `${detail.related.parent.reportNo} · ${detail.related.parent.title}`]);
  section('linked', links.length > 0 ? [pdfFacts(links)] : []);

  // 7. Implementation: yapılan işlemler ve kullanılan malzemeler
  const implementation = (details.implementation ?? null) as {
    workDone?: string | null;
    materials?: {
      item?: string | null;
      code?: string | null;
      quantity?: number | null;
      fromStock?: boolean | null;
      requisitionNo?: string | null;
    }[];
  } | null;
  const materials = implementation?.materials ?? [];
  section('implementation', [
    ...(implementation?.workDone
      ? pdfBlock(t('hse.fields.workDone'), implementation.workDone)
      : []),
    ...(materials.length > 0
      ? [
          pdfTable(
            [
              t('hse.hseq.implementation.item'),
              t('hse.hseq.implementation.code'),
              t('hse.hseq.implementation.quantity'),
              t('hse.hseq.implementation.fromStock'),
              t('hse.hseq.implementation.requisition'),
            ],
            materials.map((m) => [
              pdfText(m.item),
              pdfText(m.code),
              m.quantity === null || m.quantity === undefined ? '—' : String(m.quantity),
              m.fromStock === null || m.fromStock === undefined
                ? '—'
                : t(m.fromStock ? 'hse.yesNo.yes' : 'hse.yesNo.no'),
              pdfText(m.requisitionNo),
            ]),
            ['*', 70, 50, 50, 70],
          ),
        ]
      : []),
  ]);

  // 8. Verification: etkinlik, tarih, doğrulayan, açıklama
  const verification = (details.verification ?? null) as { effective?: string | null } | null;
  section('verification', [
    ...(verification?.effective
      ? [
          pdfFacts([
            [t('hse.hseq.verification.effective'), t(`hse.yesNo.${verification.effective}`)],
          ]),
        ]
      : []),
    ...fieldContent(verificationSpecs(type)),
  ]);

  // 9. Management / DPA Review: ofis notları (karar onay izinde)
  section(
    'review',
    detail.officeNotes.length > 0
      ? [
          pdfTable(
            [k('name'), k('kind'), k('reason')],
            detail.officeNotes.map((n) => [
              `${nameOf(n.by) ?? '—'}\n${pdfLtUtc(n.at, null)}`,
              t(`hse.noteKinds.${n.kind}`),
              n.body,
            ]),
            [110, 90, '*'],
          ),
        ]
      : [],
  );

  // 10. Closure: SMS prosedür güncelleme ihtiyacı (H10); kapanış kararları onay izinde
  const sms = (details.smsUpdate ?? null) as {
    needed?: boolean | null;
    procedure?: string | null;
    proposal?: string | null;
  } | null;
  section(
    'closure',
    sms?.needed
      ? pdfBlock(
          t('hse.hseq.sms.title'),
          [sms.procedure, sms.proposal].filter((x): x is string => Boolean(x)).join('\n') || '—',
        )
      : [],
  );

  // Onay izi
  const approvals: RecordPdfApproval[] = [];
  if (detail.submittedAt) {
    approvals.push({
      step: k('submitted'),
      name: nameOf(detail.createdBy),
      role: roleNames(detail.parties?.submittedRoleIds),
      at: detail.submittedAt,
      offsetMin: offset,
    });
  }
  if (report.reviewedAt) {
    approvals.push({
      step: k('reviewed'),
      name: nameOf(report.reviewedBy),
      role: roleNames(detail.parties?.reviewedRoleIds),
      at: report.reviewedAt,
      offsetMin: offset,
      note: report.reviewNote,
    });
  }
  const steps = detail.approval.steps.length;
  const events = [
    ...detail.approval.submissions.map((s) => ({
      at: s.at,
      row: {
        step: k('closureSubmitted', { revision: s.revision }),
        name: nameOf(s.by),
        role: roleNames(s.roleIds),
        at: s.at,
        offsetMin: offset,
      } as RecordPdfApproval,
    })),
    ...detail.approval.decisions.map((d) => ({
      at: d.at,
      row: {
        step:
          d.decision === 'approved'
            ? k('closureApproved')
            : d.step > steps
              ? k('reopened')
              : k('closureReturned'),
        name: nameOf(d.by),
        role: `${detail.roles[d.roleId] ?? '—'}${d.office ? ` (${k('office')})` : ''}`,
        at: d.at,
        // Ofis adımı ofiste verilir: yalnız UTC
        offsetMin: d.office ? null : offset,
        note: d.reason,
      } as RecordPdfApproval,
    })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  approvals.push(...events.map((e) => e.row));

  const marks: string[] = [];
  if (detail.status === 'draft') marks.push(t('recordPdf.draftMark'));
  if (detail.status === 'reopened') marks.push(t('recordPdf.reopenedMark'));
  if (detail.status === 'closed') marks.push(t('recordPdf.closedMark'));
  if (hidden.size > 0) marks.push(t('recordPdf.restrictedMark'));

  return recordPdf(t, {
    brand: input.brand,
    title: `${t(`hse.types.${type}`)} — ${detail.title}`,
    number: detail.reportNo,
    marks,
    subject: vessel.name,
    content,
    approvals,
    printedAt: input.printedAt,
  });
}
